import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    TextField, Button, Stack, Typography, Alert, Box, Autocomplete, CircularProgress,
    MenuItem, FormControlLabel, Checkbox
} from '@mui/material';
import { crearTramiteMinusvalia, getTramitesMinusvalia, getRegularidadMinusvalia } from '../../services/minusvaliaService';
import { buscarClientes, getClientePorCuil } from '../../services/clienteService';
import ModalRegistrarCliente from '../ModalRegistrarCliente';
import SeccionGenerarPacto from '../Pacto/SeccionGenerarPacto';
import usePactosPendientes from '../Pacto/usePactosPendientes';
import RegularidadMinusvalia from '../RegularidadMinusvalia';
import { useAuth } from '../../auth/AuthContext';
import { apellidoNombre } from '../../utils/nombrePersona';
import CampoFechaLiquidar from '../Tramite/CampoFechaLiquidar';
import PageHeader from '../ui/PageHeader';

/**
 * Formulario para iniciar un trámite de Minusvalía. Nace en "Ver regularidad": la regularidad de
 * aportes se muestra acá como aviso informativo (no bloquea el alta, la evalúa la junta médica
 * más adelante en el ciclo).
 */
export default function FormularioIniciarTramiteMinusvalia({ onSuccess, onCancel, cuilInicial, idTurnoOrigen }) {
    const navigate = useNavigate();
    const { registrarPacto, adjuntarATramite } = usePactosPendientes();
    const { user } = useAuth();
    const { cuil: cuilParam } = useParams();
    const cuilEfectivo = cuilInicial || cuilParam;

    const [form, setForm] = useState({
        idCliente: '',
        fechaInicioEstimada: '',
        idUsuario: user?.id || null,
        organismo: '',
        patologia: '',
        cuota: '',
        ddjjSalud: false,
        aportesProvincia: false,
        comentarios: '',
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [guardando, setGuardando] = useState(false);

    // --- Búsqueda de cliente ---
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [opcionesCliente, setOpcionesCliente] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [modalClienteOpen, setModalClienteOpen] = useState(false);
    const busquedaTimeoutRef = useRef(null);

    // --- Trámite de Minusvalía duplicado para el mismo cliente ---
    const [tieneDuplicadoMinusvalia, setTieneDuplicadoMinusvalia] = useState(false);
    const [cantidadDuplicados, setCantidadDuplicados] = useState(0);
    const [cargandoDuplicados, setCargandoDuplicados] = useState(false);
    const [confirmarDuplicado, setConfirmarDuplicado] = useState(false);

    // --- Regularidad de aportes (aviso informativo, no bloquea el alta) ---
    const [regularidad, setRegularidad] = useState(null);
    const [cargandoRegularidad, setCargandoRegularidad] = useState(false);

    // Carga inicial de cliente si viene por URL
    useEffect(() => {
        const cargar = async () => {
            if (!cuilEfectivo) return;
            try {
                const cliente = await getClientePorCuil(cuilEfectivo);
                setClienteSeleccionado(cliente);
                setForm(prev => ({ ...prev, idCliente: cliente.cuil }));
            } catch (err) {
                console.error('Error al cargar cliente inicial:', err);
            }
        };
        cargar();
    }, [cuilEfectivo]);

    // Trámite de Minusvalía ya existente para el cliente seleccionado
    useEffect(() => {
        const cuilCliente = form.idCliente;
        if (!cuilCliente) {
            setTieneDuplicadoMinusvalia(false);
            setCantidadDuplicados(0);
            setConfirmarDuplicado(false);
            setRegularidad(null);
            return;
        }

        let activo = true;
        const verificarDuplicado = async () => {
            setCargandoDuplicados(true);
            try {
                const tramites = await getTramitesMinusvalia(cuilCliente);
                const items = Array.isArray(tramites) ? tramites : [];
                if (activo) {
                    setCantidadDuplicados(items.length);
                    setTieneDuplicadoMinusvalia(items.length > 0);
                    if (items.length === 0) {
                        setConfirmarDuplicado(false);
                    }
                }
            } catch (err) {
                console.error('Error validando trámites de Minusvalía duplicados:', err);
                if (activo) {
                    setTieneDuplicadoMinusvalia(false);
                    setCantidadDuplicados(0);
                }
            } finally {
                if (activo) setCargandoDuplicados(false);
            }
        };
        verificarDuplicado();

        const cargarRegularidad = async () => {
            setCargandoRegularidad(true);
            try {
                const data = await getRegularidadMinusvalia(cuilCliente);
                if (activo) setRegularidad(data);
            } catch (err) {
                console.error('Error al calcular regularidad de minusvalía:', err);
                if (activo) setRegularidad(null);
            } finally {
                if (activo) setCargandoRegularidad(false);
            }
        };
        cargarRegularidad();

        return () => { activo = false; };
    }, [form.idCliente]);

    const handleBusquedaCliente = (_event, value) => {
        if (!value) { setOpcionesCliente([]); setBuscando(false); return; }
        setBuscando(true);
        if (busquedaTimeoutRef.current) clearTimeout(busquedaTimeoutRef.current);
        busquedaTimeoutRef.current = setTimeout(async () => {
            try {
                const resultados = await buscarClientes(value);
                setOpcionesCliente(resultados);
            } catch (err) {
                console.error('Error al buscar clientes', err);
            } finally {
                setBuscando(false);
            }
        }, 300);
    };

    const handleSeleccionCliente = (_event, newValue) => {
        setClienteSeleccionado(newValue);
        setForm(prev => ({ ...prev, idCliente: newValue ? newValue.cuil : '' }));
    };

    const handleRegistroExitoso = (clienteNuevo) => {
        setClienteSeleccionado(clienteNuevo);
        setForm(prev => ({ ...prev, idCliente: clienteNuevo.cuil }));
        setModalClienteOpen(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleChangeCheckbox = (e) => {
        const { name, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: checked }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.idCliente) { setError('Debe seleccionar un cliente.'); return; }
        if (tieneDuplicadoMinusvalia && !confirmarDuplicado) {
            setError('Debe confirmar que desea crear otro trámite de Minusvalía para un cliente que ya tiene uno.');
            return;
        }
        setError('');
        setSuccess('');
        setGuardando(true);
        try {
            const payload = {
                idCliente: form.idCliente,
                idUsuario: form.idUsuario,
                organismo: form.organismo || null,
                patologia: form.patologia,
                cuota: form.cuota,
                ddjjSalud: form.ddjjSalud,
                aportesProvincia: form.aportesProvincia,
                comentarios: form.comentarios,
                fechaInicioEstimada: form.fechaInicioEstimada || null,
                idTurnoOrigen: idTurnoOrigen || null,
            };
            const creado = await crearTramiteMinusvalia(payload);
            // El pacto se emitió antes de que el trámite existiera: recién ahora se puede adjuntar.
            await adjuntarATramite(creado.idTramite);
            setSuccess(`Trámite de Minusvalía creado con éxito. ID: ${creado.idTramite}`);
            if (onSuccess) {
                onSuccess(creado);
            } else {
                navigate(`/minusvalia/tramites/${creado.idTramite}`);
            }
        } catch (err) {
            setError(err.response?.data || err.response?.data?.message || 'Error al crear el trámite.');
        } finally {
            setGuardando(false);
        }
    };

    const seccionTitulo = (icono, texto) => (
        <div style={{
            background: 'var(--primary-bg)',
            padding: '0.75rem 1.25rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--primary-border)', marginTop: '0.5rem'
        }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--primary-text)', fontSize: 'var(--fs-texto-lg)' }}>
                <i className={`bi ${icono} me-2`}></i>{texto}
            </Typography>
        </div>
    );

    return (
        <div className="area-rti">
            <Box sx={{ background: 'var(--bg-page)', minHeight: '100vh', padding: 3 }}>
                <Box sx={{ maxWidth: 900, margin: 'auto', background: 'white', borderRadius: '16px', boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
                    <PageHeader
                        variante="banner"
                        icono="person-wheelchair"
                        titulo="Iniciar Trámite de Minusvalía"
                        descripcion="Jubilación por invalidez/discapacidad: complete los datos para registrar el trámite"
                    />

                    <Box sx={{ padding: '2rem' }}>
                    <ModalRegistrarCliente open={modalClienteOpen} onClose={() => setModalClienteOpen(false)} onSuccess={handleRegistroExitoso} mostrarDialogoConfirmacion={false} />

                    <form onSubmit={handleSubmit}>
                        <Stack spacing={3}>
                            {/* Cliente */}
                            {seccionTitulo('bi-person-check', 'Seleccionar Cliente')}
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <Autocomplete
                                    id="buscar-cliente-minusvalia"
                                    options={opcionesCliente}
                                    getOptionLabel={(option) => `${apellidoNombre(option.apellido, option.nombre)} (${option.cuil})`}
                                    onInputChange={handleBusquedaCliente}
                                    onChange={handleSeleccionCliente}
                                    value={clienteSeleccionado}
                                    isOptionEqualToValue={(option, value) => option.cuil === value.cuil}
                                    noOptionsText="No se encontraron clientes"
                                    loading={buscando}
                                    loadingText="Buscando..."
                                    sx={{ flexGrow: 1 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Buscar cliente por CUIL, Apellido o Nombre"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {buscando ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                                <Button variant="outlined" onClick={() => setModalClienteOpen(true)} sx={{ height: '56px', flexShrink: 0 }}>
                                    Registrar Cliente
                                </Button>
                            </Box>

                            <SeccionGenerarPacto cliente={clienteSeleccionado} onPactoGenerado={registrarPacto} />

                            {form.idCliente && (
                                <RegularidadMinusvalia regularidad={regularidad} cargando={cargandoRegularidad} cuil={form.idCliente} />
                            )}

                            {/* Datos propios del trámite */}
                            {seccionTitulo('bi-file-earmark-text', 'Datos del Trámite')}

                            <TextField
                                select
                                label="Organismo"
                                name="organismo"
                                value={form.organismo}
                                onChange={handleChange}
                                fullWidth
                                helperText="Seleccione si el trámite se gestiona ante Provincia o ANSES."
                            >
                                <MenuItem value=""><em>Sin especificar</em></MenuItem>
                                <MenuItem value="PROVINCIA">Provincia</MenuItem>
                                <MenuItem value="ANSES">ANSES</MenuItem>
                            </TextField>

                            <TextField
                                label="Patología"
                                name="patologia"
                                value={form.patologia}
                                onChange={handleChange}
                                fullWidth
                            />

                            <Stack direction="row" spacing={2}>
                                <TextField label="Cuota" name="cuota" value={form.cuota} onChange={handleChange} fullWidth />
                            </Stack>
                            <Stack direction="row" spacing={3}>
                                <FormControlLabel
                                    control={<Checkbox name="ddjjSalud" checked={form.ddjjSalud} onChange={handleChangeCheckbox} />}
                                    label="DDJJ de salud presentada"
                                />
                                <FormControlLabel
                                    control={<Checkbox name="aportesProvincia" checked={form.aportesProvincia} onChange={handleChangeCheckbox} />}
                                    label="Aporta en provincia"
                                />
                            </Stack>

                            <CampoFechaLiquidar
                                valor={form.fechaInicioEstimada}
                                onChange={(v) => setForm((f) => ({ ...f, fechaInicioEstimada: v }))}
                            />

                            <TextField label="Observaciones" name="comentarios" value={form.comentarios} onChange={handleChange} multiline rows={4} fullWidth
                                helperText="Particularidades del caso (lo que se le deja anotado al equipo)." />

                            {error && <Alert severity="error" sx={{ borderRadius: '10px' }}>{String(error)}</Alert>}
                            {success && <Alert severity="success" sx={{ borderRadius: '10px' }}>{success}</Alert>}
                            {tieneDuplicadoMinusvalia && (
                                <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                                    Este cliente ya tiene {cantidadDuplicados === 1 ? 'un trámite de Minusvalía' : `${cantidadDuplicados} trámites de Minusvalía`}. ¿Está seguro de que desea crear otro?
                                </Alert>
                            )}
                            {tieneDuplicadoMinusvalia && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={confirmarDuplicado}
                                            onChange={(e) => setConfirmarDuplicado(e.target.checked)}
                                        />
                                    }
                                    label="Confirmo que deseo continuar con un nuevo trámite de Minusvalía igualmente."
                                />
                            )}
                            {cargandoDuplicados && <Alert severity="info" sx={{ borderRadius: '10px' }}>Verificando trámites de Minusvalía activos del cliente...</Alert>}

                            <Stack direction="row" spacing={2} sx={{ width: '100%', marginTop: '1rem' }}>
                                <Button fullWidth variant="outlined" size="large"
                                    onClick={() => onCancel ? onCancel() : navigate('/tramites')}
                                    sx={{ color: '#dc2626', border: '2px solid #fecaca', borderRadius: '10px', textTransform: 'none' }}>
                                    <i className="bi bi-x-circle me-2"></i>Cancelar
                                </Button>
                                <Button fullWidth type="submit" size="large" disabled={!form.idCliente || guardando}
                                    sx={{
                                        background: 'var(--primary)', color: 'white', borderRadius: '10px',
                                        textTransform: 'none', fontWeight: 600,
                                        '&:hover': { background: 'var(--primary-hover)' },
                                        '&:disabled': { background: '#e5e7eb', color: '#9ca3af' }
                                    }}>
                                    {guardando ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <><i className="bi bi-check-circle me-2"></i>Iniciar Trámite</>}
                                </Button>
                            </Stack>
                        </Stack>
                    </form>
                </Box>
            </Box>
        </Box>
    </div>
);
}
